import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Loader from '../common/Loader';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketProvider';

// 1. Create the Context
export const SettingsContext = createContext(null);

const SETTINGS_CACHE_KEY = 'cachedSettingsData';

// 2. Create the Provider component
export const SettingsProvider = ({ children }) => {
    // Completely remove destructuring to guarantee no crashes
    const authContext = useAuth();
    const user = authContext ? authContext.user : null;
    const authLoading = authContext ? authContext.loading : false;
    
    const socket = useSocket();
    
    // State for Settings AND Stock
    const [settings, setSettings] = useState(null);
    const [stockItems, setStockItems] = useState([]); // ⭐ NEW: Global Stock State
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch both Settings and Stock
    const fetchGlobalData = useCallback(async () => {
        // If there is no user (e.g. public embed), skip fetching authenticated data
        if (authLoading || !user) {
            setIsLoading(false);
            setSettings(null);
            setStockItems([]);
            return;
        }

        try {
            // ⭐ OPTIMIZATION: Fetch both in parallel
            const [settingsRes, stockRes] = await Promise.all([
                api.get('/settings'),
                api.get('/stock')
            ]);

            const freshSettings = settingsRes.data;
            const freshStockItems = stockRes.data || [];

            setSettings(freshSettings);
            setStockItems(freshStockItems); // Ensure it defaults to array
            setIsLoading(false);
            setError(null);

            // ⭐ APP-LEVEL OFFLINE CACHE (iOS native): Service workers aren't reliably
            // available inside Capacitor's WKWebView on iOS, so a full app close
            // (not just backgrounding) rebuilds the WebView from scratch with no
            // in-memory state to fall back on. Without a cached copy, `settings`
            // stays null indefinitely on a cold start offline — which cascades
            // into downstream logic that treats "null" as "still loading" (e.g.
            // isJourneyButtonsHidden in StaffJobCard.js), silently hiding buttons
            // like Start Journey/Clock In that should be showing.
            try {
                localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({
                    settings: freshSettings,
                    stockItems: freshStockItems,
                    cachedAt: Date.now()
                }));
            } catch (e) {
                console.warn('Settings cache save failed:', e);
            }
        } catch (err) {
            console.error("Failed to fetch global data:", err);

            // ⭐ Try the app-level cache before giving up. This is what makes a
            // full cold start offline on iOS behave the same as a soft
            // close/reopen — settings resolve to the last known real values
            // instead of sitting in permanent "loading" limbo as null.
            let servedFromCache = false;
            try {
                const cachedRaw = localStorage.getItem(SETTINGS_CACHE_KEY);
                if (cachedRaw) {
                    const { settings: cachedSettings, stockItems: cachedStockItems, cachedAt } = JSON.parse(cachedRaw);
                    if (cachedSettings) {
                        setSettings(cachedSettings);
                        setStockItems(cachedStockItems || []);
                        servedFromCache = true;
                        console.warn(`Settings fetch failed — served cached settings from ${new Date(cachedAt).toLocaleString()}.`);
                    }
                }
            } catch (cacheErr) {
                console.warn('Settings cache read failed:', cacheErr);
            }

            if (!servedFromCache) {
                setError("Failed to load application data.");
                toast.error("Failed to load application settings.");
            }
            setIsLoading(false);
        }
    }, [user, authLoading]);

    // Initial Fetch
    useEffect(() => {
        fetchGlobalData();
    }, [fetchGlobalData]);

    // Socket Listeners for Real-time Updates
    useEffect(() => {
        if (socket && typeof socket.on === 'function') {
            const handleSettingsUpdate = (data) => {
                console.log('Socket event "settings-update" received!', data);
                toast.info('Settings updated. Refreshing...');
                fetchGlobalData(); 
            };

            const handleStockUpdate = (data) => {
                console.log('Socket event "stock-update" received!', data);
                // Optional: Toast for stock might be too noisy, so we just refresh silently or with a subtle log
                fetchGlobalData(); 
            };
            
            socket.on('settings-update', handleSettingsUpdate);
            socket.on('stock-update', handleStockUpdate); // ⭐ NEW: Listen for stock changes

            return () => {
                socket.off('settings-update', handleSettingsUpdate);
                socket.off('stock-update', handleStockUpdate);
            };
        }
    }, [socket, fetchGlobalData]);

    return (
        <SettingsContext.Provider value={{ 
            settings, 
            setSettings, 
            stockItems,       // ⭐ EXPOSED
            setStockItems,   // ⭐ EXPOSED
            isLoading, 
            error, 
            fetchSettings: fetchGlobalData // Renamed alias for compatibility
        }}>
            {isLoading && !settings ? (
                <div className="flex items-center justify-center min-h-screen">
                    <Loader />
                </div>
            ) : (
                children
            )}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        // ⭐ FIX 3: Return a safe default instead of throwing a fatal error
        console.warn('useSettings must be used within a SettingsProvider. Using fallbacks.');
        return { settings: null, stockItems: [] };
    }
    return context;
};